import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, increment } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useLike } from '../../utils/likeProvider';
import { useSave } from '../../utils/saveProvider';
import { useSubscribe } from '../../utils/subscribeProvider';
import { CommentSection } from '../../ui/screens/post/widgets/CommentSection';
import UserAvatar from '../../components/UserAvatar';
import VerificationBadge from '../../components/VerificationBadge';
import { Heart, MessageCircle, Bookmark, Send, Plus, Pause, MapPin, Loader2 } from 'lucide-react';

const ORANGE = '#FF8C08';
const BG = '#08090B';

const DEFAULT_BOTTOM_NAV_HEIGHT = 56; // matches kBottomNavigationBarHeight

function fmtDuration(seconds) {
  const s = Math.floor(seconds % 60);
  const m = Math.floor(seconds / 60) % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function priceText(listing) {
  const price = listing.price;
  const isNeg = listing.isNegotiable === true;
  if (isNeg || price == null) return 'Negotiable';
  if (typeof price === 'number') {
    return `UGX ${Math.round(price)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }
  return String(price);
}

/**
 * VideoItem — TikTok-style vertical video feed item.
 *
 * Props:
 * - listing: object (Firestore listing data, including `id`)
 * - isActive: boolean — whether this is the current feed item (unmuted, playable)
 * - bottomNavBarHeight: number (px) — default 56
 */
export default function VideoItem({ listing, isActive, bottomNavBarHeight = DEFAULT_BOTTOM_NAV_HEIGHT }) {
  const navigate = useNavigate();
  const like = useLike();
  const save = useSave();
  const subscribe = useSubscribe();

  const containerRef = useRef(null);
  const videoRef = useRef(null);

  const [videoReady, setVideoReady] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [visibleOnScreen, setVisibleOnScreen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const [progress, setProgress] = useState({ position: 0, duration: 0 });
  const [showCommentSection, setShowCommentSection] = useState(false);

  const [userData, setUserData] = useState({
    displayName: 'Unknown',
    profileImage: '',
    verified: false,
  });

  const listingId = listing.id?.toString() || '';
  const ownerUid = listing.uid?.toString() || listing.userRef?.toString() || '';
  const listingTitle = listing.title?.toString() || 'your listing';
  const thumbnailUrl = listing.thumbnailUrl?.toString() || '';
  const firstImageUrl = Array.isArray(listing.imageUrls) && listing.imageUrls.length > 0
    ? listing.imageUrls[0].toString()
    : null;

  const isLiked = like.isLiked(listingId);
  const isSaved = save.isSaved(listingId);
  const isSubscribedToOwner = ownerUid ? subscribe.isSubscribedTo(ownerUid) : false;

  // ── Subscribe to owner's user doc ────────────────────────────────────
  useEffect(() => {
    if (!ownerUid) return undefined;
    const unsub = onSnapshot(doc(db, 'users', ownerUid), (snap) => {
      if (!snap.exists()) return;
      const d = snap.data();
      setUserData({
        displayName: d.displayName?.toString() || d.name?.toString() || 'Unknown',
        profileImage: d.profileImage?.toString() || d.photoURL?.toString() || '',
        // Firestore stores this field as `isVerified`, not `verified`.
        // Falling back to `verified` too, in case some older docs use that key.
        verified: d.isVerified === true || d.verified === true,
      });
    });
    return unsub;
  }, [ownerUid]);

  // ── Visibility detection (equivalent of VisibilityDetector) ──────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setVisibleOnScreen(entry.intersectionRatio > 0.7),
      { threshold: [0, 0.7, 1] }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ── Apply active/visible state to the video element ──────────────────
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoReady) return;

    if (isActive && visibleOnScreen) {
      v.muted = false;
      if (!isPaused) v.play().catch(() => {});
    } else if (!isActive) {
      v.muted = true;
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [isActive, visibleOnScreen, videoReady, isPaused]);

  const togglePlayPause = () => {
    const v = videoRef.current;
    if (!v) return;
    if (!v.paused) {
      v.pause();
      setIsPaused(true);
      setShowProgress(true);
    } else {
      v.play().catch(() => {});
      setIsPaused(false);
      setShowProgress(false);
    }
  };

  const rewind = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, v.currentTime - 5);
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setProgress({ position: v.currentTime, duration: v.duration || 0 });
  };

  const handleEnded = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play().catch(() => {});
  };

  // ── Social actions ────────────────────────────────────────────────────
  const handleLike = useCallback(async () => {
    if (!auth.currentUser || !listingId) return;
    await like.toggleLike({
      listingId,
      listingOwnerId: ownerUid,
      listingTitle,
      thumbnailUrl: thumbnailUrl || firstImageUrl,
      listingImageUrl: firstImageUrl,
    });
  }, [like, listingId, ownerUid, listingTitle, thumbnailUrl, firstImageUrl]);

  const handleSave = useCallback(async () => {
    if (!auth.currentUser || !listingId) return;
    await save.toggleSave({
      listingId,
      listingOwnerId: ownerUid,
      listingTitle,
      thumbnailUrl: thumbnailUrl || firstImageUrl,
      listingImageUrl: firstImageUrl,
    });
  }, [save, listingId, ownerUid, listingTitle, thumbnailUrl, firstImageUrl]);

  const handleSubscribeToggle = async () => {
    if (!auth.currentUser || !ownerUid) return;
    await subscribe.toggleSubscribe(ownerUid);
  };

  const handleShare = async () => {
    setIsSharing(true);
    try {
      const title = listing.title || 'this property';
      const url = `https://filatom.com/listing/${listingId}`;
      const text = `Check out "${title}" on Filatom!\n${url}`;
      if (navigator.share) {
        await navigator.share({ title, text, url });
      } else {
        await navigator.clipboard.writeText(text);
      }
      await updateDoc(doc(db, 'listings', listingId), { shareCount: increment(1) });
    } catch (_) {
      // ignore, matches Flutter's silent catch
    } finally {
      setIsSharing(false);
    }
  };

  const openListing = () => {
    if (!listingId) return;
    updateDoc(doc(db, 'listings', listingId), { viewCount: increment(1) }).catch(() => {});
    navigate(`/listing/${listingId}`);
  };

  const openOwnerProfile = () => {
    if (ownerUid) navigate(`/profile/${ownerUid}`);
  };

  const category = (listing.subcategory ?? listing.category)?.toString() || '';
  const district = listing.district?.toString() || '';
  const division = listing.division?.toString() || '';
  const locationText = [district, division].filter(Boolean).join(', ');
  const title = listing.title?.toString() || '';
  const description = listing.description?.toString() || '';

  const navBarClearance = bottomNavBarHeight > 0 ? bottomNavBarHeight + 13 : 16;
  const actionColBottom = navBarClearance + 10;
  const infoAreaBottom = showProgress ? navBarClearance + 56 : navBarClearance + 10;
  const progressBarBottom = navBarClearance;

  return (
    <div
      ref={containerRef}
      onClick={togglePlayPause}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: BG,
        overflow: 'hidden',
      }}
    >
      {/* Video layer */}
      {listing.videoUrl ? (
        <video
          ref={videoRef}
          src={listing.videoUrl}
          poster={thumbnailUrl || undefined}
          loop
          playsInline
          muted={!isActive}
          onLoadedData={() => setVideoReady(true)}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'absolute',
            inset: 0,
          }}
        />
      ) : thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
        />
      ) : (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader2 color={ORANGE} size={28} className="spin" />
        </div>
      )}

      {/* Top gradient */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 140,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.55), transparent)',
          pointerEvents: 'none',
        }}
      />

      {/* Bottom gradient */}
      <div
        style={{
          position: 'absolute',
          bottom: navBarClearance,
          left: 0,
          right: 0,
          height: 300,
          background: 'linear-gradient(to top, rgba(0,0,0,0.80), transparent)',
          pointerEvents: 'none',
        }}
      />

      {/* Pause indicator */}
      {isPaused && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              width: 70,
              height: 70,
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.50)',
              opacity: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Pause color="#fff" size={38} fill="#fff" />
          </div>
        </div>
      )}

      {/* Double-tap-to-rewind zone (left third) */}
      <div
        onDoubleClick={(e) => {
          e.stopPropagation();
          rewind();
        }}
        style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '33%' }}
      />

      {/* Action column */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'absolute', right: 12, bottom: actionColBottom }}
      >
        <ActionColumn
          userData={userData}
          ownerUid={ownerUid}
          isSubscribedToOwner={isSubscribedToOwner}
          onOpenProfile={openOwnerProfile}
          onSubscribeToggle={handleSubscribeToggle}
          isLiked={isLiked}
          isSaved={isSaved}
          isSharing={isSharing}
          onLike={handleLike}
          onComments={() => setShowCommentSection(true)}
          onSave={handleSave}
          onShare={handleShare}
        />
      </div>

      {/* Info area */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'absolute', left: 14, right: 72, bottom: infoAreaBottom }}
      >
        <InfoArea
          userData={userData}
          category={category}
          onOpenProfile={openOwnerProfile}
          priceText={priceText(listing)}
          title={title}
          description={description}
          descExpanded={descExpanded}
          onToggleDesc={() => (descExpanded ? setDescExpanded(false) : openListing())}
          locationText={locationText}
        />
      </div>

      {/* Progress bar */}
      {showProgress && videoReady && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'absolute', left: 0, right: 0, bottom: progressBarBottom, padding: '0 14px' }}
        >
          <ProgressBar
            position={progress.position}
            duration={progress.duration}
            onSeek={(ratio) => {
              const v = videoRef.current;
              if (v && progress.duration) v.currentTime = ratio * progress.duration;
            }}
          />
        </div>
      )}

      {showCommentSection && listingId && (
        <CommentSection listingId={listingId} onClose={() => setShowCommentSection(false)} />
      )}

      <style>{`
        .spin { animation: videoitem-spin 1s linear infinite; }
        @keyframes videoitem-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

function ActionColumn({
  userData,
  ownerUid,
  isSubscribedToOwner,
  onOpenProfile,
  onSubscribeToggle,
  isLiked,
  isSaved,
  isSharing,
  onLike,
  onComments,
  onSave,
  onShare,
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div onClick={onOpenProfile} style={{ position: 'relative', cursor: 'pointer' }}>
        <UserAvatar size={48} imageUrl={userData.profileImage || undefined} name={userData.displayName} />
        {!isSubscribedToOwner && ownerUid && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSubscribeToggle();
            }}
            style={{
              position: 'absolute',
              bottom: -10,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 22,
              height: 22,
              borderRadius: '50%',
              backgroundColor: ORANGE,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <Plus color="#fff" size={14} />
          </button>
        )}
      </div>

      <div style={{ height: 28 }} />
      <SideButton icon={Heart} isActive={isLiked} activeColor="#FF1493" onClick={onLike} filled={isLiked} />
      <div style={{ height: 22 }} />
      <SideButton icon={MessageCircle} isActive={false} activeColor={ORANGE} onClick={onComments} />
      <div style={{ height: 22 }} />
      <SideButton icon={Bookmark} isActive={isSaved} activeColor="#4DA3FF" onClick={onSave} filled={isSaved} />
      <div style={{ height: 22 }} />
      {isSharing ? (
        <div style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader2 color="rgba(255,255,255,0.38)" size={20} className="spin" />
        </div>
      ) : (
        <SideButton icon={Send} isActive={false} activeColor="#fff" onClick={onShare} />
      )}
    </div>
  );
}

function SideButton({ icon: Icon, isActive, activeColor, onClick, filled = false }) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        backgroundColor: 'rgba(0,0,0,0.35)',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      <Icon
        color={isActive ? activeColor : 'rgba(255,255,255,0.90)'}
        fill={isActive && filled ? activeColor : 'none'}
        size={24}
      />
    </button>
  );
}

function InfoArea({
  userData,
  category,
  onOpenProfile,
  priceText: price,
  title,
  description,
  descExpanded,
  onToggleDesc,
  locationText,
}) {
  const shortDesc = description.length > 60 ? description.slice(0, 60) : description;
  const textShadow = '0 0 6px rgba(0,0,0,0.54)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      {category && (
        <>
          <span
            style={{
              padding: '4px 10px',
              borderRadius: 20,
              backgroundColor: 'rgba(255,140,8,0.20)',
              border: `1px solid rgba(255,140,8,0.45)`,
              color: ORANGE,
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {category}
          </span>
          <div style={{ height: 8 }} />
        </>
      )}

      <div onClick={onOpenProfile} style={{ cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <span
            style={{
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              textShadow,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 220,
            }}
          >
            {userData.displayName}
          </span>
          {userData.verified && (
            <>
              <div style={{ width: 4, flexShrink: 0 }} />
              <VerificationBadge size={14} />
            </>
          )}
        </div>
        <div
          style={{
            marginTop: 3,
            display: 'inline-block',
            padding: '3px 8px',
            backgroundColor: 'rgba(0,0,0,0.40)',
            borderRadius: 6,
            color: ORANGE,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {price}
        </div>
      </div>

      <div style={{ height: 8 }} />

      {title && (
        <p
          style={{
            margin: 0,
            color: '#fff',
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1.3,
            textShadow,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {title}
        </p>
      )}

      {description && (
        <>
          <div style={{ height: 4 }} />
          <p
            onClick={onToggleDesc}
            style={{
              margin: 0,
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.85)',
              fontSize: 13,
              lineHeight: 1.4,
              textShadow: '0 0 4px rgba(0,0,0,0.54)',
              display: '-webkit-box',
              WebkitLineClamp: descExpanded ? 5 : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {descExpanded ? description : shortDesc}
            {!descExpanded && description.length > 60 && (
              <span style={{ color: '#fff', fontWeight: 600 }}>... more</span>
            )}
          </p>
        </>
      )}

      <div style={{ height: 8 }} />

      {locationText && (
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <MapPin color={ORANGE} size={14} />
          <div style={{ width: 4 }} />
          <span
            style={{
              color: 'rgba(255,255,255,0.75)',
              fontSize: 12,
              textShadow: '0 0 4px rgba(0,0,0,0.54)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {locationText}
          </span>
        </div>
      )}
    </div>
  );
}

function ProgressBar({ position, duration, onSeek }) {
  const trackRef = useRef(null);
  const progress = duration > 0 ? Math.min(1, Math.max(0, position / duration)) : 0;
  const remaining = Math.max(0, duration - position);

  const seekFromClientX = (clientX) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    onSeek(ratio);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: 'rgba(255,255,255,0.70)', fontSize: 11 }}>{fmtDuration(position)}</span>
        <span style={{ color: 'rgba(255,255,255,0.70)', fontSize: 11 }}>-{fmtDuration(remaining)}</span>
      </div>
      <div style={{ height: 4 }} />
      <div
        ref={trackRef}
        onMouseDown={(e) => seekFromClientX(e.clientX)}
        onTouchStart={(e) => seekFromClientX(e.touches[0].clientX)}
        style={{
          height: 20,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        <div style={{ width: '100%', height: 3, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.24)' }}>
          <div style={{ width: `${progress * 100}%`, height: '100%', borderRadius: 2, backgroundColor: ORANGE }} />
        </div>
      </div>
    </div>
  );
}

export { VideoItem };